import { BaseService } from '../base/BaseService';

import { reconcileStaleGenerations } from './conceptRenditionCorrelation';

import type { Json } from '@/integrations/supabase/types';
import { seedGeometry } from '@/lib/concept-workbench/propagation';
import { RATIO_STAGE } from '@/lib/concept-workbench/stage-dimensions';
import type {
  AspectRatioToken,
  LayerOverride,
  SetLayer,
  VariantFraming,
  VariantLayerGeometry,
  VariantSetDocument,
  VariantState,
} from '@/lib/concept-workbench/types';
import {
  type Platform,
  getPlacement,
  toMatrixPlatform,
} from '@/lib/platform-matrix/placement-matrix';

/**
 * Persistence for the Concept Workbench variant-set model.
 *
 * Two rows back one document:
 *
 *   • `concept_rendition_sets` — ONE row per Concept, holding the set document
 *     (`{ layers, fontScaleBase, mediaId }`). These are the PROPAGATING
 *     properties: one copy for the whole set.
 *   • `concept_renditions`     — one row per (Concept, aspect ratio), holding the
 *     per-ratio state that must never propagate: geometry, overrides, framing,
 *     the image link, and the two Ready-gate flags.
 *
 * 🔴 **Nothing here may write `project_tasks`.** The migration
 * (`20260812030000_concept_renditions.sql`) moved the set document off that table
 * precisely because `advance_brief_concept_version` bumps the Concept's
 * optimistic-lock `version` on EVERY update to a `creative-concept` row. Layer
 * state autosaves on an 800ms debounce; routing it through `project_tasks` would
 * bump the brief's version several times a second and make the brief's explicit
 * Save fail with a spurious P0001. Spec §6.8: "Layer state is never affected by a
 * brief conflict — different rows, different model."
 *
 * 🔴 **Identity.** Both tables are ACCOUNT-SCOPED
 * (`src/lib/identity/table-scope.generated.ts`), so every read filter and every
 * insert carries `resolve_account_owner(actor)` — never the raw session id. The
 * resolution happens once, in the hook, via `resolveOwnerId()`; this service
 * takes the resolved id as an explicit argument so a call site cannot forget it
 * by omission. (`AdUnitsService.listAdUnits` established the same shape.)
 */

export const CONCEPT_RENDITION_SET_COLUMNS =
  'id, project_task_id, rendition_set, version, updated_at';

export const CONCEPT_RENDITION_COLUMNS =
  'id, project_task_id, aspect_ratio, media_id, overlay_layers, layer_overrides, framing, follows_set_image, needs_review, is_generating, position, version, updated_at, generation_token, generation_started_at';

/**
 * The default ratio set for a Concept that has none.
 *
 * Spec §8.4 is explicit: seed "from `placement-matrix.ts`'s `recommendedRatio`
 * for the concept's declared platform". Authoring a size nobody will publish
 * costs a real generation (image quota has already been a live blocker), so the
 * set is derived from where the Concept actually ships rather than from a fixed
 * trio.
 *
 * The fallback below is used ONLY when the Concept declares no platform at all,
 * and matches the §6.2 empty-state wireframe (1:1 · 4:5 · 9:16) — the square,
 * the feed portrait, and the vertical story, which between them cover every
 * platform in the matrix at least approximately.
 */
export const FALLBACK_DEFAULT_RATIOS: readonly AspectRatioToken[] = ['1:1', '4:5', '9:16'];

export function defaultRatiosForPlatforms(
  platforms: readonly (string | null | undefined)[],
): AspectRatioToken[] {
  const ratios: AspectRatioToken[] = [];
  for (const raw of platforms) {
    const platform: Platform | undefined = toMatrixPlatform(raw);
    if (!platform) continue;
    const row = getPlacement(platform);
    if (!row) continue;
    if (!ratios.includes(row.recommendedRatio)) ratios.push(row.recommendedRatio);
  }
  return ratios.length > 0 ? ratios : [...FALLBACK_DEFAULT_RATIOS];
}

/** One layer's geometry as stored inside `concept_renditions.overlay_layers`. */
export interface StoredLayerGeometry extends VariantLayerGeometry {
  id: string;
}

export interface StoredRenditionSet {
  layers: SetLayer[];
  fontScaleBase: number;
  mediaId: string | null;
}

interface RenditionSetRow {
  id: string;
  project_task_id: string;
  rendition_set: Json;
  version: number;
  updated_at: string;
}

interface RenditionRow {
  id: string;
  project_task_id: string;
  aspect_ratio: string;
  media_id: string | null;
  overlay_layers: Json;
  layer_overrides: Json;
  framing: Json;
  follows_set_image: boolean;
  needs_review: boolean;
  is_generating: boolean;
  position: number;
  version: number;
  updated_at: string;
  generation_token: string | null;
  generation_started_at: string | null;
}

/**
 * What load() had to repair to make the two rows agree. Surfaced rather than
 * swallowed: a set document and its rendition rows disagreeing is a real event,
 * and "it silently looked fine" is the bug class this repo keeps shipping.
 */
export interface ReconciliationReport {
  /** No `concept_rendition_sets` row existed; the document was read as empty. */
  missingSetRow: boolean;
  /** Rendition rows whose `aspect_ratio` is not a known token — skipped entirely. */
  unknownRatios: string[];
  /** Geometry entries for layers the set no longer has, per rendition. */
  droppedGeometry: { renditionId: string; layerIds: string[] }[];
  /** Overrides for layers the set no longer has, per rendition. */
  droppedOverrides: { renditionId: string; layerIds: string[] }[];
  /** Layers present in the set but absent from a rendition — seeded on read. */
  seededGeometry: { renditionId: string; layerIds: string[] }[];
}

export interface LoadedVariantSet {
  document: VariantSetDocument;
  /** Null when the Concept has no `concept_rendition_sets` row yet. */
  setRowId: string | null;
  /** 0 when there is no set row — no version to lock against. */
  setVersion: number;
  reconciliation: ReconciliationReport;
}

/** A field-scoped patch of the set-level document. */
export interface SetPatch {
  layers?: SetLayer[];
  fontScaleBase?: number;
  mediaId?: string | null;
}

/** A field-scoped patch of one rendition row. */
export interface RenditionPatch {
  mediaId?: string | null;
  followsSetImage?: boolean;
  needsReview?: boolean;
  isGenerating?: boolean;
  position?: number;
  framing?: VariantFraming;
  geometry?: Record<string, VariantLayerGeometry>;
  overrides?: Record<string, LayerOverride>;
  /** THE GENERATION CORRELATION FIELDS. See the migration + `setGenerating`. */
  generationToken?: string | null;
  generationStartedAt?: string | null;
}

/** Thrown when a CAS write lost twice. The caller must not clobber; it decides. */
export class ConceptRenditionConflictError extends Error {
  constructor(
    readonly table: 'concept_rendition_sets' | 'concept_renditions',
    readonly rowId: string,
    readonly expectedVersion: number,
    readonly serverVersion: number,
  ) {
    super(
      `${table} row ${rowId} changed elsewhere (expected version ${expectedVersion}, server has ${serverVersion}).`,
    );
    this.name = 'ConceptRenditionConflictError';
  }
}

/** Thrown when the row is gone, or RLS filtered it out — never silently ignored. */
export class ConceptRenditionMissingError extends Error {
  constructor(
    readonly table: 'concept_rendition_sets' | 'concept_renditions',
    readonly rowId: string,
  ) {
    super(`${table} row ${rowId} is missing or not visible to this account.`);
    this.name = 'ConceptRenditionMissingError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isKnownRatio(value: string): value is AspectRatioToken {
  return Object.prototype.hasOwnProperty.call(RATIO_STAGE, value);
}

function parseSetDocument(raw: Json): StoredRenditionSet {
  if (!isRecord(raw)) return { layers: [], fontScaleBase: 1, mediaId: null };
  const layers = Array.isArray(raw.layers) ? (raw.layers as unknown as SetLayer[]) : [];
  const fontScaleBase = typeof raw.fontScaleBase === 'number' ? raw.fontScaleBase : 1;
  const mediaId = typeof raw.mediaId === 'string' ? raw.mediaId : null;
  return { layers, fontScaleBase, mediaId };
}

function parseGeometry(raw: Json): Record<string, VariantLayerGeometry> {
  if (!Array.isArray(raw)) return {};
  const geometry: Record<string, VariantLayerGeometry> = {};
  for (const entry of raw as unknown as StoredLayerGeometry[]) {
    if (!isRecord(entry) || typeof entry.id !== 'string') continue;
    const { id, ...rest } = entry;
    geometry[id] = rest as VariantLayerGeometry;
  }
  return geometry;
}

function serializeGeometry(geometry: Record<string, VariantLayerGeometry>): Json {
  return Object.entries(geometry).map(([id, value]) => ({ id, ...value })) as unknown as Json;
}

function parseOverrides(raw: Json): Record<string, LayerOverride> {
  return isRecord(raw) ? (raw as unknown as Record<string, LayerOverride>) : {};
}

function parseFraming(raw: Json): VariantFraming {
  return isRecord(raw) ? (raw as unknown as VariantFraming) : {};
}

export class ConceptRenditionService extends BaseService {
  /**
   * Read the set document + every rendition for a Concept and hydrate the store's
   * `VariantSetDocument`.
   *
   * ── THE RECONCILIATION RULE ──────────────────────────────────────────────
   * The two rows can disagree, because they are written independently. One rule
   * decides, and it follows directly from §7.1 ("the variant SET is the
   * document"):
   *
   *   **The set row is authoritative for layer MEMBERSHIP. Rendition rows are
   *   authoritative for everything per-ratio.**
   *
   * Concretely:
   *   • A geometry entry or override for a layer the set no longer has is
   *     DROPPED — the layer was deleted, and set membership wins.
   *   • A set layer a rendition has no geometry for is SEEDED with
   *     `seedGeometry()`, the same deterministic function `addLayer` uses, so a
   *     newly-added layer appears in every ratio at its default placement.
   *   • An `aspect_ratio` outside `AspectRatioToken` is skipped, not guessed.
   *   • A missing set row hydrates as an empty document.
   *
   * A read NEVER writes the repair back. Persisting on open would bump `version`
   * on every load — including a read-only viewer's — and manufacture the exact
   * spurious conflicts the CAS below exists to avoid. The repair is deterministic,
   * so it re-derives identically next time and lands on the first real edit.
   */
  /**
   * The leading rendered image each of these Concepts holds, in ONE query.
   *
   * Built for the ad-copy library, where a page can list dozens of ad units
   * across a handful of Concepts and every card wants to know whether artwork
   * exists. `load()` is per-Concept and drags the whole variant document with
   * it; hooks cannot be looped, so a page could not have called it N times even
   * if the payload were free.
   *
   * Ordered by `position` and reduced to the FIRST rendition that carries a
   * media id, which is the same choice `useAdUnitCreative` documents: an ad
   * unit is one ad, the replica draws one placement, and which ratio actually
   * ships is decided per-placement at composite time — not here.
   *
   * `ownerId` is the RESOLVED account owner, same contract as every other read
   * on this service. Returns only Concepts that have one, so a missing key and
   * a null value both mean "no artwork", and neither means "not loaded" — the
   * caller's `isPending` carries that.
   */
  async listLeadingMediaIds(
    conceptIds: readonly string[],
    ownerId: string,
  ): Promise<Record<string, string>> {
    if (conceptIds.length === 0) return {};

    const { data, error } = await this.client
      .from('concept_renditions')
      .select('project_task_id, media_id, position')
      .in('project_task_id', [...new Set(conceptIds)])
      .eq('user_id', ownerId)
      .not('media_id', 'is', null)
      .order('position', { ascending: true });

    if (error) throw new Error(error.message);

    const leading: Record<string, string> = {};
    for (const row of (data ?? []) as Array<{
      project_task_id: string | null;
      media_id: string | null;
    }>) {
      if (!row.project_task_id || !row.media_id) continue;
      if (leading[row.project_task_id]) continue;
      leading[row.project_task_id] = row.media_id;
    }
    return leading;
  }

  /**
   * The aspect ratios each of these Concepts has renditions for, in ONE query.
   *
   * WHY THIS EXISTS. marketer.com's ad card ends in a footer that reads
   * `Static · 🖼 1:1 · 🖼 4:5` — the format, then the ratios that ad brief
   * actually declares (`OBSERVED-2026-08-27-marketer-briefs-and-ad-journey.md`
   * §3.5, evidence `71-brief-detail-overlay-dialog.png`). The founder named
   * the ratio as one of the four things he can read off their card at a
   * glance, so the Brief's own ad cards have to carry it. `concept_renditions`
   * has held `aspect_ratio` all along; nothing on a list surface read it.
   *
   * Same shape and same contract as `listLeadingMediaIds` above: one `.in()`
   * over the whole grid rather than a per-card hook, `ownerId` is the RESOLVED
   * account owner, and a Concept with no renditions is simply ABSENT from the
   * result — a missing key means "no rendition", never "not loaded", which the
   * caller's `isPending` carries instead.
   *
   * Ordered by `position` so the ratios read in the order the workbench shows
   * them, and de-duplicated: two renditions at `4:5` are two artboards, not
   * two ratios.
   */
  /**
   * The two facets a Brief's ad-card grid needs from `concept_renditions`, in
   * ONE query: which ratios each Concept has artboards for, and the media id of
   * the first artboard that actually holds an image.
   *
   * 🔴 THE COVER IS HERE BECAUSE THE CARD HAD TWO JUDGES OF ONE STATE. The card
   * printed its ratio chips from THIS table and its media slot from
   * `project_files` (`BriefWorkspaceService.currentMedia`), which is a different
   * grain — uploads and produced files bound to the task, not the Concept's
   * rendered artboards. Measured on `apegsjpotzvxumdedwty` 2026-08-27, Concept
   * `451873c6-…`: 3 renditions, one of them (`1:1`, v4) carrying
   * `media_id 47a2fe71-…`, and **zero** `project_files` rows. So the same card
   * printed `· 1:1 · 4:5 · 9:16` in its footer and a dashed **Add creative**
   * slot 300px above it — telling a marketer who has finished artwork that they
   * have none, over a control that sends them off to make more.
   *
   * `project_files` is still preferred by the card when it has something,
   * because an uploaded/produced file is the more specific fact. This is the
   * fallback that stops the absence of one source reading as the absence of the
   * creative.
   */
  async listRenditionFacetsByConcept(
    conceptIds: readonly string[],
    ownerId: string,
  ): Promise<{
    ratios: Record<string, string[]>;
    coverMediaIds: Record<string, string>;
    readyByConceptId: Record<string, boolean>;
  }> {
    if (conceptIds.length === 0) {
      return { ratios: {}, coverMediaIds: {}, readyByConceptId: {} };
    }

    const { data, error } = await this.client
      .from('concept_renditions')
      .select('project_task_id, aspect_ratio, position, media_id, needs_review, is_generating')
      .in('project_task_id', [...new Set(conceptIds)])
      .eq('user_id', ownerId)
      .order('position', { ascending: true });

    if (error) throw new Error(error.message);

    const ratios: Record<string, string[]> = {};
    const coverMediaIds: Record<string, string> = {};
    const readyByConceptId: Record<string, boolean> = {};
    for (const row of (data ?? []) as Array<{
      project_task_id: string | null;
      aspect_ratio: string | null;
      media_id: string | null;
      needs_review: boolean;
      is_generating: boolean;
    }>) {
      if (!row.project_task_id) continue;
      // Same verdict as `evaluateReadyGate`: an empty set is not ready, and
      // every rendition must have media, be settled, and have passed review.
      readyByConceptId[row.project_task_id] =
        (readyByConceptId[row.project_task_id] ?? true) &&
        Boolean(row.media_id) &&
        !row.is_generating &&
        !row.needs_review;
      // First by `position`, so the cover is the artboard the Concept itself
      // orders first — never whichever row the database happened to return.
      if (row.media_id && !coverMediaIds[row.project_task_id]) {
        coverMediaIds[row.project_task_id] = row.media_id;
      }
      if (!row.aspect_ratio) continue;
      const existing = ratios[row.project_task_id] ?? [];
      if (existing.includes(row.aspect_ratio)) continue;
      ratios[row.project_task_id] = [...existing, row.aspect_ratio];
    }
    return { ratios, coverMediaIds, readyByConceptId };
  }

  async load(conceptId: string, ownerId: string): Promise<LoadedVariantSet> {
    const [setResult, renditionResult] = await Promise.all([
      this.client
        .from('concept_rendition_sets')
        .select(CONCEPT_RENDITION_SET_COLUMNS)
        .eq('project_task_id', conceptId)
        .eq('user_id', ownerId)
        .maybeSingle(),
      this.client
        .from('concept_renditions')
        .select(CONCEPT_RENDITION_COLUMNS)
        .eq('project_task_id', conceptId)
        .eq('user_id', ownerId)
        .order('position', { ascending: true }),
    ]);

    if (setResult.error) throw new Error(setResult.error.message);
    if (renditionResult.error) throw new Error(renditionResult.error.message);

    const setRow = (setResult.data ?? null) as RenditionSetRow | null;
    const rows = (renditionResult.data ?? []) as unknown as RenditionRow[];
    return hydrateVariantSet(conceptId, setRow, rows);
  }

  /**
   * Make sure a Concept has a set row and at least the requested ratios.
   *
   * Idempotent by construction: the set row is upserted on its UNIQUE
   * `project_task_id`, and renditions are inserted only for ratios that are
   * missing, which the `(project_task_id, aspect_ratio)` UNIQUE constraint backs
   * up. Two tabs opening the same Concept therefore converge instead of racing.
   */
  async ensureRenditions(input: {
    conceptId: string;
    ownerId: string;
    ratios: readonly AspectRatioToken[];
  }): Promise<LoadedVariantSet> {
    const existing = await this.load(input.conceptId, input.ownerId);

    if (!existing.setRowId) {
      const { error } = await this.client.from('concept_rendition_sets').upsert(
        {
          project_task_id: input.conceptId,
          user_id: input.ownerId,
          rendition_set: { layers: [], fontScaleBase: 1, mediaId: null } as unknown as Json,
        },
        { onConflict: 'project_task_id' },
      );
      if (error) throw new Error(error.message);
    }

    const present = new Set(existing.document.variants.map((variant) => variant.aspectRatio));
    const missing = input.ratios.filter((ratio) => !present.has(ratio));
    if (missing.length > 0) {
      let position = existing.document.variants.length;
      const { error } = await this.client.from('concept_renditions').upsert(
        missing.map((ratio) => ({
          project_task_id: input.conceptId,
          user_id: input.ownerId,
          aspect_ratio: ratio,
          overlay_layers: serializeGeometry(seededGeometryFor(existing.document.layers, ratio)),
          position: position++,
        })),
        { onConflict: 'project_task_id,aspect_ratio', ignoreDuplicates: true },
      );
      if (error) throw new Error(error.message);
    }

    return this.load(input.conceptId, input.ownerId);
  }

  /** Add one ratio to an existing set. Returns the hydrated variant. */
  async addRatio(input: {
    conceptId: string;
    ownerId: string;
    aspectRatio: AspectRatioToken;
    position: number;
    layers: readonly SetLayer[];
    mediaId?: string | null;
  }): Promise<VariantState> {
    const { data, error } = await this.client
      .from('concept_renditions')
      .insert({
        project_task_id: input.conceptId,
        user_id: input.ownerId,
        aspect_ratio: input.aspectRatio,
        media_id: input.mediaId ?? null,
        overlay_layers: serializeGeometry(seededGeometryFor(input.layers, input.aspectRatio)),
        position: input.position,
      })
      .select(CONCEPT_RENDITION_COLUMNS)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) {
      // RLS filters a forbidden write out rather than raising, so "no error and
      // no row" is what an unauthorised insert looks like. Returning quietly
      // would report a rendition that does not exist.
      throw new Error('That size could not be added. Reload the concept and try again.');
    }
    return hydrateVariant(data as unknown as RenditionRow, [], true).variant;
  }

  /** Remove one ratio. Deleting is not version-locked: the row is going away. */
  async removeRatio(renditionId: string, ownerId: string): Promise<void> {
    const { error } = await this.client
      .from('concept_renditions')
      .delete()
      .eq('id', renditionId)
      .eq('user_id', ownerId);
    if (error) throw new Error(error.message);
  }

  /** Persist a field-scoped patch of the set document under optimistic locking. */
  async saveSet(input: {
    setRowId: string;
    expectedVersion: number;
    patch: SetPatch;
    /** The full current document — `rendition_set` is one JSONB column. */
    current: StoredRenditionSet;
  }): Promise<{ version: number; document: StoredRenditionSet }> {
    const next: StoredRenditionSet = {
      layers: input.patch.layers ?? input.current.layers,
      fontScaleBase: input.patch.fontScaleBase ?? input.current.fontScaleBase,
      mediaId: input.patch.mediaId === undefined ? input.current.mediaId : input.patch.mediaId,
    };
    const row = await this.compareAndSwap<RenditionSetRow>({
      table: 'concept_rendition_sets',
      columns: CONCEPT_RENDITION_SET_COLUMNS,
      rowId: input.setRowId,
      expectedVersion: input.expectedVersion,
      values: { rendition_set: next as unknown as Json },
    });
    return { version: row.version, document: parseSetDocument(row.rendition_set) };
  }

  /** Persist a field-scoped patch of one rendition under optimistic locking. */
  async saveRendition(input: {
    renditionId: string;
    expectedVersion: number;
    patch: RenditionPatch;
  }): Promise<{ version: number; row: RenditionRow }> {
    const values = renditionPatchToColumns(input.patch);
    if (Object.keys(values).length === 0) {
      throw new Error('A rendition save needs at least one field to write.');
    }
    const row = await this.compareAndSwap<RenditionRow>({
      table: 'concept_renditions',
      columns: CONCEPT_RENDITION_COLUMNS,
      rowId: input.renditionId,
      expectedVersion: input.expectedVersion,
      values,
    });
    return { version: row.version, row };
  }

  /**
   * The Ready gate's second condition, persisted.
   *
   * Deliberately NOT debounced: it is low-frequency (once at dispatch, once at
   * completion) and the gate reads it. An in-memory-only flag evaporates on
   * reload and a refreshed tab would offer `Ready` on a Concept that is still
   * generating — a false pass, which §6.9 forbids outright.
   *
   * `generationToken` is stamped ONLY on the dispatch call (`isGenerating:
   * true`), never on the completion call — see `useWorkbenchPrompt.submit`,
   * which mints it before the request goes out and never repeats it in its
   * `finally`. This is THE durable-correlation half of the fix: the token
   * (plus `generationStartedAt`) is what lets
   * `attachGeneratedMediaByCorrelation` (called from inside
   * `generate-image.post.ts` itself, not only from the client's fetch
   * promise) find and complete the right row even if nothing in the browser
   * is still listening when the provider finishes.
   */
  setGenerating(input: {
    renditionId: string;
    expectedVersion: number;
    isGenerating: boolean;
    generationToken?: string;
  }): Promise<{ version: number; row: RenditionRow }> {
    return this.saveRendition({
      renditionId: input.renditionId,
      expectedVersion: input.expectedVersion,
      patch: {
        isGenerating: input.isGenerating,
        ...(input.isGenerating && input.generationToken
          ? {
              generationToken: input.generationToken,
              generationStartedAt: new Date().toISOString(),
            }
          : {}),
      },
    });
  }

  /**
   * Flip any rendition that has been `is_generating` for longer than any real
   * provider call could still be running back to an honest failed state.
   *
   * Uses the CALLER's client (the browser's RLS-scoped session in every real
   * caller today — see `useConceptWorkbench`'s mount/poll effect), never a
   * service role: every row this can see is already scoped to `ownerId` by
   * `user_id`. Deliberately does NOT bump `version` — this only clears a
   * stuck flag, never a user-authored field, so it cannot collide with the
   * CAS a real edit is racing against.
   */
  async reconcileStaleGenerations(input: {
    conceptId: string;
    ownerId: string;
    timeoutMs: number;
  }): Promise<string[]> {
    return reconcileStaleGenerations(this.client, input);
  }

  /**
   * Bind an already-persisted library item to one rendition only. This is kept
   * separate from the set image path: a generated paid creative belongs to the
   * ratio the user selected, never to every size in the concept.
   *
   * Reading first makes a recovery retry idempotent when the original request
   * reached Postgres but its response was lost. The actual write still goes
   * through the rendition CAS, so a concurrent canvas edit cannot be silently
   * overwritten.
   */
  async attachMedia(input: {
    renditionId: string;
    ownerId: string;
    expectedVersion: number;
    mediaId: string;
  }): Promise<{ version: number; row: RenditionRow }> {
    const { data, error } = await this.client
      .from('concept_renditions')
      .select(CONCEPT_RENDITION_COLUMNS)
      .eq('id', input.renditionId)
      .eq('user_id', input.ownerId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data) throw new ConceptRenditionMissingError('concept_renditions', input.renditionId);

    const current = data as unknown as RenditionRow;
    if (current.media_id === input.mediaId && !current.follows_set_image) {
      return { version: current.version, row: current };
    }

    return this.saveRendition({
      renditionId: input.renditionId,
      expectedVersion: input.expectedVersion,
      patch: { mediaId: input.mediaId, followsSetImage: false },
    });
  }

  /**
   * ── OPTIMISTIC-LOCK CONFLICT RESOLUTION: field-scoped rebase, bounded ─────
   *
   * The write is a CAS: `UPDATE … SET <patched columns>, version = expected + 1
   * WHERE id = $1 AND version = $2`. PostgREST returns zero rows when the guard
   * fails, so a lost race is observable rather than silent — no `.upsert`, no
   * blind overwrite.
   *
   * On a miss we do NOT clobber and we do NOT discard the user's work. We refetch
   * the row and retry the SAME patch against the server's current version. Because
   * an UPDATE only touches the columns it names, this REBASES: the concurrent
   * writer's other columns survive, and only the fields this save actually owns
   * are overwritten.
   *
   * Why rebase rather than "surface a conflict dialog", the choice §6.8 leaves
   * open for the brief: the brief is a shared reviewed document where a silent
   * merge could publish someone else's words, so it interrupts. Layer state is
   * private, high-frequency, and non-destructive — but its row is ALSO written by
   * a background image generation completing (`media_id`, `is_generating`), which
   * is the common conflict and is not a human disagreement at all. Interrupting a
   * marketer to say "your drag collided with your own image finishing" would be
   * absurd; blind last-write-wins on the whole row would drop the new `media_id`
   * and leave the canvas showing a stale image. Field-scoped rebase is the only
   * option that keeps both.
   *
   * Bounded to ONE retry. A second miss means a genuinely hot row, and looping is
   * how an autosave becomes a write storm; the caller gets a
   * `ConceptRenditionConflictError` carrying both versions and decides.
   */
  private async compareAndSwap<Row extends { id: string; version: number }>(input: {
    table: 'concept_rendition_sets' | 'concept_renditions';
    columns: string;
    rowId: string;
    expectedVersion: number;
    values: Record<string, unknown>;
  }): Promise<Row> {
    let expected = input.expectedVersion;

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const { data, error } = await this.client
        .from(input.table)
        .update({ ...input.values, version: expected + 1 })
        .eq('id', input.rowId)
        .eq('version', expected)
        .select(input.columns)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (data) return data as unknown as Row;

      const { data: fresh, error: refetchError } = await this.client
        .from(input.table)
        .select('id, version')
        .eq('id', input.rowId)
        .maybeSingle();
      if (refetchError) throw new Error(refetchError.message);
      if (!fresh) throw new ConceptRenditionMissingError(input.table, input.rowId);

      const serverVersion = (fresh as { version: number }).version;
      if (attempt === 1 || serverVersion === expected) {
        // `serverVersion === expected` means the guard matched but no row came
        // back — RLS, not a race. Reporting that as a conflict would send the
        // user round a reload loop that can never succeed.
        throw new ConceptRenditionConflictError(
          input.table,
          input.rowId,
          input.expectedVersion,
          serverVersion,
        );
      }
      expected = serverVersion;
    }

    throw new ConceptRenditionConflictError(
      input.table,
      input.rowId,
      input.expectedVersion,
      input.expectedVersion,
    );
  }
}

function renditionPatchToColumns(patch: RenditionPatch): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  if (patch.mediaId !== undefined) values.media_id = patch.mediaId;
  if (patch.followsSetImage !== undefined) values.follows_set_image = patch.followsSetImage;
  if (patch.needsReview !== undefined) values.needs_review = patch.needsReview;
  if (patch.isGenerating !== undefined) values.is_generating = patch.isGenerating;
  if (patch.position !== undefined) values.position = patch.position;
  if (patch.framing !== undefined) values.framing = patch.framing as unknown as Json;
  if (patch.geometry !== undefined) values.overlay_layers = serializeGeometry(patch.geometry);
  if (patch.overrides !== undefined) values.layer_overrides = patch.overrides as unknown as Json;
  if (patch.generationToken !== undefined) values.generation_token = patch.generationToken;
  if (patch.generationStartedAt !== undefined) {
    values.generation_started_at = patch.generationStartedAt;
  }
  return values;
}

function seededGeometryFor(
  layers: readonly SetLayer[],
  ratio: AspectRatioToken,
): Record<string, VariantLayerGeometry> {
  const geometry: Record<string, VariantLayerGeometry> = {};
  for (const layer of layers) geometry[layer.id] = seedGeometry(layer, ratio);
  return geometry;
}

function hydrateVariant(
  row: RenditionRow,
  layers: readonly SetLayer[],
  /**
   * True only when the SET ROW ITSELF could not be read. An existing set with
   * `layers: []` means every layer was genuinely deleted and the rendition's
   * stale geometry must go; a MISSING set row means we do not know what the
   * membership is, and deleting a rendition's placement on the strength of a
   * failed read would be destructive.
   */
  preserveUnknownLayers: boolean,
): {
  variant: VariantState;
  dropped: string[];
  droppedOverrides: string[];
  seeded: string[];
} {
  const storedGeometry = parseGeometry(row.overlay_layers);
  const storedOverrides = parseOverrides(row.layer_overrides);
  const ratio = row.aspect_ratio as AspectRatioToken;

  const known = new Set(layers.map((layer) => layer.id));
  const dropped = Object.keys(storedGeometry).filter((id) => !known.has(id));
  const droppedOverrides = Object.keys(storedOverrides).filter((id) => !known.has(id));
  const seeded: string[] = [];

  const geometry: Record<string, VariantLayerGeometry> = {};
  const overrides: Record<string, LayerOverride> = {};
  for (const layer of layers) {
    const stored = storedGeometry[layer.id];
    if (stored) {
      geometry[layer.id] = stored;
    } else {
      geometry[layer.id] = seedGeometry(layer, ratio);
      seeded.push(layer.id);
    }
    const override = storedOverrides[layer.id];
    if (override) overrides[layer.id] = override;
  }

  const variant: VariantState = {
    id: row.id,
    aspectRatio: ratio,
    position: row.position,
    mediaId: row.media_id,
    followsSetImage: row.follows_set_image,
    needsReview: row.needs_review,
    isGenerating: row.is_generating,
    version: row.version,
    framing: parseFraming(row.framing),
    geometry: preserveUnknownLayers ? storedGeometry : geometry,
    overrides: preserveUnknownLayers ? storedOverrides : overrides,
  };

  if (preserveUnknownLayers) return { variant, dropped: [], droppedOverrides: [], seeded: [] };
  return { variant, dropped, droppedOverrides, seeded };
}

/** Exported for tests: the pure half of `load()`. */
export function hydrateVariantSet(
  conceptId: string,
  setRow: { id: string; rendition_set: Json; version: number } | null,
  rows: readonly RenditionRow[],
): LoadedVariantSet {
  const stored = setRow
    ? parseSetDocument(setRow.rendition_set)
    : { layers: [], fontScaleBase: 1, mediaId: null };

  const reconciliation: ReconciliationReport = {
    missingSetRow: setRow === null,
    unknownRatios: [],
    droppedGeometry: [],
    droppedOverrides: [],
    seededGeometry: [],
  };

  const variants: VariantState[] = [];
  for (const row of rows) {
    if (!isKnownRatio(row.aspect_ratio)) {
      reconciliation.unknownRatios.push(row.aspect_ratio);
      continue;
    }
    const result = hydrateVariant(row, stored.layers, setRow === null);
    if (result.dropped.length > 0) {
      reconciliation.droppedGeometry.push({ renditionId: row.id, layerIds: result.dropped });
    }
    if (result.droppedOverrides.length > 0) {
      reconciliation.droppedOverrides.push({
        renditionId: row.id,
        layerIds: result.droppedOverrides,
      });
    }
    if (result.seeded.length > 0) {
      reconciliation.seededGeometry.push({ renditionId: row.id, layerIds: result.seeded });
    }
    variants.push(result.variant);
  }

  variants.sort((a, b) => a.position - b.position);

  return {
    document: {
      conceptId,
      mediaId: stored.mediaId,
      fontScaleBase: stored.fontScaleBase,
      layers: stored.layers,
      variants,
    },
    setRowId: setRow?.id ?? null,
    setVersion: setRow?.version ?? 0,
    reconciliation,
  };
}

export const conceptRenditionService = new ConceptRenditionService();

export {
  attachGeneratedMediaByCorrelation,
  reconcileStaleGenerations,
} from './conceptRenditionCorrelation';
